/**
 * Single source of truth for CDN URLs.
 *
 * Everything now lives in one R2 bucket served from cdn.tcgfan.com, laid out as:
 *
 *   games/<game>/images/<lang>/<id>.webp      card art
 *   games/<game>/images/set-squares/          set / format squares
 *   games/<game>/images/archetype-squares/
 *   games/<game>/images/leader-squares/
 *   games/<game>/images/leader-aa/
 *   games/<game>/data/...                     card data JSON
 *   tcgplayer/images/<tcgplayerId>.webp       cross-game product photos
 *   shared/logo/...                           site chrome
 *
 * There is no "/original/" or "/webp/" segment any more: every stored object is
 * a full-size webp, and imgproxy derives every other size and format on demand.
 *
 * NEXT_PUBLIC_CDN (the old cdn.gumgum.gg host) is deliberately not used here —
 * One Piece assets moved to cdn.tcgfan.com along with everything else.
 */

export const CDN = (
  process.env.NEXT_PUBLIC_TCGFAN_CDN || "https://cdn.tcgfan.com"
).replace(/\/+$/, "");

/** Game folder names as they appear under games/. */
export type CdnGame =
  "one-piece" | "union-arena" | "riftbound" | "naruto" | "gundam" | "palworld";

const version = (v?: string | number) => (v ? `?v=${v}` : "");

/** games/<game>/images/<lang>/<file> — card art. `lang` is lowercased. */
export function cardImageUrl(
  game: CdnGame,
  lang: string,
  file: string,
  v?: string | number,
): string {
  return `${CDN}/games/${game}/images/${lang.toLowerCase()}/${file}${version(v)}`;
}

/** games/<game>/images/<folder>/<file> — squares and other per-game assets. */
export function gameAssetUrl(
  game: CdnGame,
  folder: string,
  file: string,
  v?: string | number,
): string {
  return `${CDN}/games/${game}/images/${folder}/${file}${version(v)}`;
}

/** games/<game>/data/<path> */
export function gameDataUrl(
  game: CdnGame,
  path: string,
  v?: string | number,
): string {
  return `${CDN}/games/${game}/data/${path}${version(v)}`;
}

/** tcgplayer/images/<id>.webp — keyed by TCGplayer product id, no language. */
export function tcgplayerImageUrl(id: string): string {
  return `${CDN}/tcgplayer/images/${id}.webp`;
}

/** shared/<path> — logos and other cross-site chrome. */
export function sharedAssetUrl(path: string): string {
  return `${CDN}/shared/${path}`;
}
